import React, { useRef, useCallback, useState } from 'react';
import './LeftPanel.css';
import { addSectionTags } from '../utils/sectionTags';

async function fetchLyricsFromLrclib(title, artist) {
  const params = new URLSearchParams({ track_name: title });
  if (artist) params.set('artist_name', artist);
  const res = await fetch(`https://lrclib.net/api/search?${params.toString()}`);
  if (!res.ok) return null;
  const data = await res.json();
  // Return the first match's plainLyrics
  return data?.[0]?.plainLyrics || null;
}

function LeftPanel({ lyrics, onLyricsChange, onFileSelect, onDetect, detecting, audioFileName,
                     midiChordFileName, isAudioFile, onAiTranscribe, transcribing, rawAnalysis,
                     stemSeparate, onStemSeparateChange, onUrlImport, importingUrl,
                     onChordSheetImport, onMidiChordImport, onReset }) {
  const fileRef = useRef();
  const txtFileRef = useRef();
  const midiFileRef = useRef();
  const [lyricsSearch, setLyricsSearch] = useState('');
  const [lyricsArtist, setLyricsArtist] = useState('');
  const [fetchingLyrics, setFetchingLyrics] = useState(false);
  const [lyricsError, setLyricsError] = useState('');
  const [showLyricsSearch, setShowLyricsSearch] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlImport, setShowUrlImport] = useState(false);

  const handleFetchLyrics = useCallback(async () => {
    if (!lyricsSearch.trim()) return;
    setFetchingLyrics(true);
    setLyricsError('');
    try {
      const result = await fetchLyricsFromLrclib(lyricsSearch.trim(), lyricsArtist.trim());
      if (result) {
        onLyricsChange(addSectionTags(result));
        setShowLyricsSearch(false);
      } else {
        setLyricsError('No lyrics found. Try a different title or artist.');
      }
    } catch {
      setLyricsError('Network error fetching lyrics.');
    } finally {
      setFetchingLyrics(false);
    }
  }, [lyricsSearch, lyricsArtist, onLyricsChange]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) onFileSelect(file);
  };

  const handleTxtFile = (e) => {
    const file = e.target.files[0];
    if (file && onChordSheetImport) {
      onChordSheetImport(file);
      e.target.value = ''; // reset so same file can be re-imported
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.txt')) {
      if (onChordSheetImport) onChordSheetImport(file);
    } else if (lower.endsWith('.mid') || lower.endsWith('.midi')) {
      if (onMidiChordImport) onMidiChordImport(file);
    } else {
      onFileSelect(file);
    }
  };

  const handleMidiFile = (e) => {
    const file = e.target.files[0];
    if (file && onMidiChordImport) {
      onMidiChordImport(file);
      e.target.value = '';
    }
  };

  const handleDebugDownload = useCallback(() => {
    if (!rawAnalysis) return;
    const bpmVal = Number(rawAnalysis?.bpm);
    const bpm = Number.isFinite(bpmVal) && bpmVal > 0 ? bpmVal : null;
    const beatsPerMeasureVal = Number(rawAnalysis?.beatsPerMeasure);
    const beatsPerMeasure = Number.isFinite(beatsPerMeasureVal) && beatsPerMeasureVal > 0
      ? Math.round(beatsPerMeasureVal)
      : 4;
    const beatSec = bpm ? (60 / bpm) : null;
    const timingGrid = rawAnalysis?.timingGrid || (beatSec ? {
      bpm: Math.round(bpm),
      beatsPerMeasure,
      measureSec: Number((beatSec * beatsPerMeasure).toFixed(4)),
      beatSec: Number(beatSec.toFixed(4)),
      wholeNoteSec: Number((beatSec * 4).toFixed(4)),
      halfNoteSec: Number((beatSec * 2).toFixed(4)),
      quarterNoteSec: Number(beatSec.toFixed(4)),
      eighthNoteSec: Number((beatSec / 2).toFixed(4)),
      sixteenthNoteSec: Number((beatSec / 4).toFixed(4)),
    } : null);
    const debugPayload = {
      ...rawAnalysis,
      timingGrid,
    };
    // Keep debug output compact: omit huge beat arrays/timestamps.
    delete debugPayload.beats;
    delete debugPayload.rawBeatTimes;
    const json = JSON.stringify(debugPayload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(rawAnalysis.file || 'analysis').replace(/\.[^.]+$/, '')}-debug.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rawAnalysis]);

  return (
    <aside className="left-panel">
      {onReset && (
        <button
          className="left-panel__reset-btn"
          onClick={onReset}
          title="Reset — clear audio, lyrics, and analysis"
          aria-label="Reset session"
        >
          ↺
        </button>
      )}
      <section className="panel-section">
        <h2 className="panel-section__title">Audio</h2>
        <div
          className={`file-drop-zone ${audioFileName ? 'file-drop-zone--loaded' : ''}`}
          onClick={() => fileRef.current.click()}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && fileRef.current.click()}
          aria-label="Upload audio file"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".wav,.mp3,.mp4,.m4a"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
          {audioFileName ? (
            <>
              <span className="file-drop-zone__icon">🎵</span>
              <span className="file-drop-zone__name">{audioFileName}</span>
              <span className="file-drop-zone__hint">Click to replace</span>
            </>
          ) : (
            <>
              <span className="file-drop-zone__icon">📂</span>
              <span className="file-drop-zone__hint">Drop a <strong>.wav</strong>, <strong>.mp3</strong>, or <strong>.m4a</strong> file here, or click to browse</span>
            </>
          )}
        </div>

        {/* YouTube / URL import */}
        <div className="url-import">
          <button
            className="ai-btn ai-btn--secondary url-import__toggle"
            onClick={() => setShowUrlImport(v => !v)}
            title="Import audio from a YouTube or other supported URL"
          >
            🎬 Import from URL
          </button>
          {showUrlImport && (
            <div className="url-import__form">
              <input
                className="lyrics-search__input"
                type="url"
                placeholder="YouTube URL, SoundCloud, etc…"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onUrlImport && onUrlImport(urlInput, () => setShowUrlImport(false))}
                autoFocus
              />
              <button
                className="ai-btn"
                disabled={importingUrl || !urlInput.trim()}
                onClick={() => onUrlImport && onUrlImport(urlInput.trim(), () => { setShowUrlImport(false); setUrlInput(''); })}
              >
                {importingUrl ? <><span className="spinner" /> Importing…</> : 'Import'}
              </button>
              <p className="url-import__hint">Powered by yt-dlp. Audio is downloaded server-side — no data is sent to third parties.</p>
            </div>
          )}
        </div>

        {/* Import external MIDI chord timing */}
        <div className="url-import">
          <input
            ref={midiFileRef}
            type="file"
            accept=".mid,.midi"
            style={{ display: 'none' }}
            onChange={handleMidiFile}
          />
          <button
            className="ai-btn ai-btn--secondary url-import__toggle"
            onClick={() => midiFileRef.current.click()}
            title="Import an external MIDI file to provide chord names and chord timings"
          >
            🎹 Import MIDI Chords (.mid)
          </button>
          {midiChordFileName ? (
            <p className="url-import__hint"><strong>Using MIDI chords:</strong> {midiChordFileName}</p>
          ) : null}
        </div>

        {/* Import .txt chord sheet */}
        <div className="url-import">
          <input
            ref={txtFileRef}
            type="file"
            accept=".txt"
            style={{ display: 'none' }}
            onChange={handleTxtFile}
          />
          <button
            className="ai-btn ai-btn--secondary url-import__toggle"
            onClick={() => txtFileRef.current.click()}
            title="Import a plain-text chord sheet (.txt) to load lyrics and chords for key transposition"
          >
            📄 Import Chord Sheet (.txt)
          </button>
        </div>
      </section>

      <section className="panel-section panel-section--grow">
        <div className="panel-section__header">
          <h2 className="panel-section__title">Lyrics</h2>
          <div className="lyrics-header-btns">
            <button
              className="ai-btn ai-btn--secondary"
              onClick={() => setShowLyricsSearch(v => !v)}
              title="Search for lyrics online (lrclib.net)"
            >
              🔍 Find
            </button>
            {isAudioFile && (
              <button
                className="ai-btn"
                onClick={onAiTranscribe}
                disabled={transcribing || detecting}
                title="Auto-generate lyrics from audio using Whisper AI"
              >
                {transcribing ? <><span className="spinner" /> Transcribing…</> : '✨ AI'}
              </button>
            )}
          </div>
        </div>
        {showLyricsSearch && (
          <div className="lyrics-search">
            <input
              className="lyrics-search__input"
              type="text"
              placeholder="Song title…"
              value={lyricsSearch}
              onChange={e => setLyricsSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFetchLyrics()}
              autoFocus
            />
            <input
              className="lyrics-search__input"
              type="text"
              placeholder="Artist (optional)…"
              value={lyricsArtist}
              onChange={e => setLyricsArtist(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFetchLyrics()}
            />
            <button
              className="ai-btn"
              onClick={handleFetchLyrics}
              disabled={fetchingLyrics || !lyricsSearch.trim()}
            >
              {fetchingLyrics ? <><span className="spinner" /> Searching…</> : 'Fetch Lyrics'}
            </button>
            {lyricsError && <p className="lyrics-search__error">{lyricsError}</p>}
          </div>
        )}
        <p className="panel-section__hint">
          Add section markers like <code>[Verse 1]</code>, <code>[Chorus]</code>, etc.
        </p>
        <textarea
          className="lyrics-textarea"
          value={lyrics}
          onChange={e => onLyricsChange(e.target.value)}
          placeholder={"[Verse 1]\nAmazing grace, how sweet the sound\nThat saved a wretch like me\n\n[Chorus]\n..."}
          spellCheck={false}
        />
      </section>

      <section className="panel-section">
        {isAudioFile && (
          <label className="stem-sep-toggle" title="Use stem separation to isolate instrumental and vocal stems before chord detection/alignment. Improves accuracy on full mixes but is slower.">
            <input
              type="checkbox"
              checked={stemSeparate}
              onChange={e => onStemSeparateChange(e.target.checked)}
              disabled={detecting}
            />
            <span>Stem separation <span className="stem-sep-toggle__hint">(slower, more accurate)</span></span>
          </label>
        )}
        <button
          className="detect-btn"
          onClick={onDetect}
          disabled={detecting || (!audioFileName && !lyrics && !midiChordFileName)}
        >
          {detecting ? (
            <><span className="spinner" /> Detecting…</>
          ) : (
            '🔍 Detect Chords'
          )}
        </button>
        {rawAnalysis && (
          <button
            className="debug-btn"
            onClick={handleDebugDownload}
            title="Download raw analysis JSON: chords with timestamps, Whisper words, and alignment"
          >
            📋 Debug JSON
          </button>
        )}
      </section>
    </aside>
  );
}

export default LeftPanel;
